import { PATHWAY } from "@/constants/testIds";
import { useRuntime } from "@/lib/RuntimeContext";

function fmt(t) {
  if (!t) return "—";
  try {
    const d = new Date(t);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return "—";
  }
}

const STATUS_META = {
  active: { label: "Active", tone: "text-amber-500", dot: "bg-amber-500" },
  simulation: {
    label: "Simulation",
    tone: "text-neutral-300",
    dot: "bg-neutral-400",
  },
  unconfigured: {
    label: "Unconfigured",
    tone: "text-[#dc2626]",
    dot: "bg-[#dc2626]",
  },
  auth_rejected: {
    label: "Auth rejected",
    tone: "text-[#dc2626]",
    dot: "bg-[#dc2626]",
  },
  unreachable: {
    label: "Unreachable",
    tone: "text-[#dc2626]",
    dot: "bg-[#dc2626]",
  },
  node_error: {
    label: "Node error",
    tone: "text-[#dc2626]",
    dot: "bg-[#dc2626]",
  },
};

function metaFor(status) {
  return STATUS_META[status] || STATUS_META.unreachable;
}

export default function RuntimeStatusPanel() {
  const { status, loading } = useRuntime();
  const s = status || {};
  const m = metaFor(s.status);

  return (
    <aside
      data-testid={PATHWAY.runtimeStatusPanel}
      className="border hairline bg-[#0f0f0f]"
    >
      <div className="flex items-center justify-between border-b hairline px-5 py-4">
        <div className="flex items-center gap-3">
          <span
            className={`inline-block h-2.5 w-2.5 ${m.dot} ${
              loading ? "animate-pulse" : ""
            }`}
          />
          <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-neutral-300">
            KATLAS runtime
          </span>
        </div>
        <span
          className={`font-mono text-[10px] uppercase tracking-[0.24em] ${m.tone}`}
        >
          {m.label}
        </span>
      </div>

      <Row label="Mode" testId="mode">
        {s.mode || "—"}
      </Row>
      <Row label="Signing service" testId="signing-service">
        {s.signing_service_available ? "Available" : "Unavailable"}
      </Row>
      <Row label="Node ID" testId="panel-node">
        {s.node_id || "—"}
      </Row>
      <Row label="Runtime version" testId="panel-version">
        {s.version || "—"}
      </Row>
      <Row label="Health" testId="panel-health">
        {s.health ? s.health.replace(/_/g, " ") : "—"}
      </Row>
      <Row label="Fallback allowed" testId="panel-fallback">
        {s.fallback_allowed ? "Yes" : "No"}
      </Row>
      <Row label="Last health check" testId="panel-last-check">
        {fmt(s.last_check)}
      </Row>

      <div className="border-t hairline px-5 py-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-neutral-500">
          Configuration
        </div>
        <div className="mt-2 grid grid-cols-1 gap-2 font-mono text-[11px] sm:grid-cols-2">
          <span data-testid={PATHWAY.runtimeField("panel-mode-cfg")}>
            <span className="text-neutral-500">Mode </span>
            <span
              className={
                s.mode_configured ? "text-amber-500" : "text-[#dc2626]"
              }
            >
              {s.mode_configured ? "declared" : "undeclared"}
            </span>
          </span>
          <span data-testid={PATHWAY.runtimeField("panel-endpoint")}>
            <span className="text-neutral-500">Endpoint </span>
            <span
              className={
                s.endpoint_configured ? "text-amber-500" : "text-[#dc2626]"
              }
            >
              {s.endpoint_configured ? "configured" : "not configured"}
            </span>
          </span>
          <span data-testid={PATHWAY.runtimeField("panel-token")}>
            <span className="text-neutral-500">Bearer token </span>
            <span
              className={
                s.token_configured ? "text-amber-500" : "text-[#dc2626]"
              }
            >
              {s.token_configured ? "present" : "absent"}
            </span>
          </span>
        </div>
        <p className="mt-4 text-[12px] italic leading-relaxed text-neutral-400">
          {s.message ||
            "Runtime status not yet retrieved. Operating in simulation."}
        </p>
        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
          Bearer token and node URL are held server-side only. The browser
          never sees them.
        </p>
      </div>
    </aside>
  );
}

function Row({ label, testId, children }) {
  return (
    <div
      data-testid={PATHWAY.runtimeField(testId)}
      className="grid grid-cols-[160px_1fr] items-baseline gap-3 border-b hairline px-5 py-3"
    >
      <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-neutral-500">
        {label}
      </span>
      <span className="font-mono text-[12px] text-neutral-100">{children}</span>
    </div>
  );
}
